import { NgClass, NgOptimizedImage } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-data-picker',
  imports: [NgClass, FormsModule, NgOptimizedImage],
  templateUrl: './data-picker.html',
  styleUrl: './data-picker.scss',
})
export class DataPicker implements OnInit {
  @Input() range: [Date, Date] | null = null;
  @Output() rangeChange = new EventEmitter<[Date, Date]>();

  currentMonth: number = new Date().getMonth();
  currentYear: number = new Date().getFullYear();

  startDate: Date | null = null;
  endDate: Date | null = null;
  isCalendarOpen = false;

  // Horário (hora e minuto)
  startHour: string = '00';
  startMinute: string = '00';
  endHour: string = '00';
  endMinute: string = '00';

  constructor(private eRef: ElementRef) {}

  ngOnInit(): void {
    if (this.range) {
      [this.startDate, this.endDate] = this.range;

      if (this.startDate) {
        this.startHour = this.pad(this.startDate.getHours());
        this.startMinute = this.pad(this.startDate.getMinutes());
      }

      if (this.endDate) {
        this.endHour = this.pad(this.endDate.getHours());
        this.endMinute = this.pad(this.endDate.getMinutes());
      }

      this.currentMonth = this.startDate.getMonth();
      this.currentYear = this.startDate.getFullYear();
    }
  }

  toggleCalendar() {
    this.isCalendarOpen = !this.isCalendarOpen;
  }

  prevMonth() {
    this.currentMonth = this.currentMonth === 0 ? 11 : this.currentMonth - 1;
    if (this.currentMonth === 11) this.currentYear--;
  }

  nextMonth() {
    this.currentMonth = this.currentMonth === 11 ? 0 : this.currentMonth + 1;
    if (this.currentMonth === 0) this.currentYear++;
  }

  get monthLabel(): string {
    return new Date(this.currentYear, this.currentMonth).toLocaleString('default', {
      month: 'long',
    });
  }

  selectDate(day: number) {
    const selected = new Date(this.currentYear, this.currentMonth, day);

    if (!this.startDate || (this.startDate && this.endDate)) {
      this.startDate = selected;
      this.endDate = null;
    } else if (selected < this.startDate) {
      this.endDate = this.startDate;
      this.startDate = selected;
    } else {
      this.endDate = selected;
    }

    this.emitIfBothDatesExist();
  }

  getDaysInMonth(): (number | null)[] {
    const days: (number | null)[] = [];
    const firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay();
    const totalDays = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();

    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= totalDays; i++) days.push(i);

    return days;
  }

  isSameDate(date: Date | null, year: number, month: number, day: number): boolean {
    return date?.getDate() === day && date?.getMonth() === month && date?.getFullYear() === year;
  }

  isStart(day: number): boolean {
    return this.isSameDate(this.startDate, this.currentYear, this.currentMonth, day);
  }

  isEnd(day: number): boolean {
    return this.isSameDate(this.endDate, this.currentYear, this.currentMonth, day);
  }

  isInRange(day: number): boolean {
    if (!this.startDate || !this.endDate) return false;
    const date = new Date(this.currentYear, this.currentMonth, day);
    return date > this.startDate && date < this.endDate;
  }

  pad(n: number | string): string {
    return n.toString().padStart(2, '0');
  }

  formatDateTime(date: Date): string {
    return (
      date.toLocaleDateString('pt-BR') +
      ' ' +
      this.pad(date.getHours()) +
      ':' +
      this.pad(date.getMinutes())
    );
  }

  get formattedRange(): string {
    if (!this.startDate) return '';
    const start = this.formatDateTime(
      this.applyTime(this.startDate, this.startHour, this.startMinute)
    );
    if (!this.endDate) return start;
    const end = this.formatDateTime(this.applyTime(this.endDate, this.endHour, this.endMinute));
    return `${start} - ${end}`;
  }

  applyTime(date: Date, hour: string, minute: string): Date {
    const d = new Date(date);
    d.setHours(parseInt(hour), parseInt(minute), 0, 0);
    return d;
  }

  emitIfBothDatesExist() {
    if (this.startDate && this.endDate) {
      const start = this.applyTime(this.startDate, this.startHour, this.startMinute);
      const end = this.applyTime(this.endDate, this.endHour, this.endMinute);
      this.rangeChange.emit([start, end]);
    }
  }

  onTimeChange() {
    this.emitIfBothDatesExist();
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.isCalendarOpen = false;
    }
  }
}
